import { motion } from "framer-motion";
import { Gem, Eye, BadgeCheck, Users, FlaskConical, CreditCard, Truck } from "lucide-react";
import { BRAND } from "@/config";

const VALUES = [
  { icon: Gem, label: "Quality" },
  { icon: Eye, label: "Transparency" },
  { icon: BadgeCheck, label: "Authenticity" },
];

const FOUNDER_PHOTO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_research-precision-1/artifacts/d6581479e9aaac9b_naz%20n%20ty.webp";

const DnaHelix = ({ delay = 0 }) => {
  const RUNGS = 26;
  return (
    <div className="dna3d h-full w-full">
      {Array.from({ length: RUNGS }).map((_, i) => {
        const d = delay - i * 0.2;
        return (
          <div className="dna-rung3d" key={i} style={{ top: `${(i / (RUNGS - 1)) * 100}%` }}>
            <span className="dna-bond3d" style={{ animationDelay: `${d}s` }} />
            <span className="dna-node3d" style={{ animationDelay: `${d}s` }} />
            <span className="dna-node3d" style={{ animationDelay: `${d - 1.5}s` }} />
          </div>
        );
      })}
    </div>
  );
};

const SECTIONS = [
  {
    icon: Users,
    title: "Who We Are",
    body: [
      "PepsWorld UK is a UK-based research and development brand specialising in high-quality research peptides and related research products.",
      "We are committed to maintaining high standards across everything we offer. Our products are sourced with quality in mind, with relevant testing and product information provided where available.",
      "We believe customers deserve clear information and a straightforward experience — no unnecessary complications, no false promises and no pretending to be something we're not.",
    ],
  },
  {
    icon: FlaskConical,
    title: "Our Products",
    body: [
      "We offer a carefully selected range of research-focused products, including GHK-Cu, Retatrutide and MT-2, alongside other products as our collection develops.",
      "Each product is presented with relevant information and specifications to help researchers understand exactly what they are looking at.",
      "All products are intended strictly for lawful research purposes and are not intended for human or veterinary use.",
    ],
  },
  {
    icon: CreditCard,
    title: "Ordering & Payment",
    body: [
      "Orders can be placed directly through the PepsWorld UK website.",
      "Available payment methods, product pricing and any applicable fees are clearly displayed throughout the ordering process.",
      "We aim to make the entire experience as simple and straightforward as possible, from browsing our collection through to receiving your order.",
    ],
  },
  {
    icon: Truck,
    title: "Shipping",
    body: [
      "We understand that once an order has been placed, you want to know when it will arrive.",
      "Our aim is to process eligible orders promptly, with orders generally dispatched within 1–3 working days, subject to availability and order volumes.",
      "Delivery times can vary depending on the destination, courier and current demand. Any relevant shipping information will be communicated clearly throughout the ordering process.",
    ],
  },
];

export const About = () => (
  <section id="about" className="relative overflow-hidden py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      {/* Founder intro */}
      <div className="grid items-start gap-12 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-500">About {BRAND.name}</span>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Founded by two friends with a passion for peptide science.
          </h1>
          <div className="mt-5 space-y-4 text-slate-600">
            <p className="text-lg leading-relaxed text-slate-700">
              {BRAND.name} was founded by <span className="font-semibold text-slate-900">Tyler</span> and <span className="font-semibold text-slate-900">Nazime (Nazzy Boy)</span> with a shared passion for peptide science, research and innovation.
            </p>
            <p className="leading-relaxed">
              What started as a small project between two friends has grown into a dedicated UK research and development brand. From the beginning, our aim has been simple: to create a brand that is open, authentic, professional and always willing to help.
            </p>
            <p className="leading-relaxed">
              We take pride in building {BRAND.name} around transparency and quality, while continuing to learn, develop and push the brand forward.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative">
          {/* animated 3D DNA helixes behind photo */}
          <div className="dna-wrap pointer-events-none absolute -inset-y-12 left-[-12%] w-24 opacity-70">
            <DnaHelix delay={0} />
          </div>
          <div className="dna-wrap pointer-events-none absolute -inset-y-12 right-[-12%] w-24 opacity-70" style={{ animationDelay: "2.5s" }}>
            <DnaHelix delay={1.3} />
          </div>
          <div className="group relative mx-auto max-w-xs overflow-hidden rounded-2xl bg-white metallic-border card-hover sm:max-w-sm">
            <div className="relative aspect-[3/4] overflow-hidden bg-slate-100">
              <img src={FOUNDER_PHOTO} alt="Nazime and Tyler — PepsWorld UK founders" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ objectPosition: "50% 78%" }} />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/75 to-transparent" />
              <div className="absolute inset-x-0 bottom-4 text-center">
                <p className="font-display text-lg font-semibold text-white drop-shadow">Nazime &amp; Tyler</p>
                <p className="mt-0.5 font-mono2 text-[10px] uppercase tracking-widest text-blue-200 drop-shadow">Proud owners of PepsWorld UK</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Values */}
      <div className="mt-12 grid gap-4 sm:grid-cols-3">
        {VALUES.map((v) => (
          <div key={v.label} className="flex items-center gap-3 rounded-2xl bg-white metallic-border p-5">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-blue-50 text-blue-600"><v.icon className="h-5 w-5" /></span>
            <p className="font-display text-lg font-semibold text-slate-900">{v.label}</p>
          </div>
        ))}
      </div>

      {/* Info sections */}
      <div className="mt-16 grid gap-6 md:grid-cols-2">
        {SECTIONS.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
            className="rounded-2xl bg-white metallic-border p-7 card-hover"
          >
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-blue-50 text-blue-600"><s.icon className="h-5 w-5" /></span>
            <h2 className="mt-4 font-display text-xl font-semibold text-slate-900">{s.title}</h2>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">
              {s.body.map((p, j) => <p key={j}>{p}</p>)}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Our Promise */}
      <motion.div
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="mt-10 overflow-hidden rounded-3xl bg-slate-900 p-8 text-center sm:p-12"
      >
        <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-blue-300">Our Promise</span>
        <h2 className="mt-3 font-display text-2xl font-bold text-white sm:text-3xl">We're committed to building something we're genuinely proud of.</h2>
        <p className="mt-4 font-display text-lg font-semibold tracking-wide text-slate-200">Quality. Transparency. Authenticity.</p>
        <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-slate-400">
          That's what Tyler and Nazime set out to create — and that's what we intend to keep building.
        </p>
        <p className="mt-6 font-wordmark text-xl tracking-tight text-white">{BRAND.name}.</p>
        <p className="mt-1 font-mono2 text-xs uppercase tracking-[0.35em] text-blue-300">{BRAND.slogan}</p>
      </motion.div>
    </div>
  </section>
);

export default About;
